import React, { useMemo } from "react";

const ServicesTeaser = () => {
  const items = useMemo(
    () => [
      {
        title: "Training",
        subtitle: "Upskill teams fast",
        desc: "Practical programs built for adoption, consistency, and measurable execution.",
        href: "/services",
        badge: "Programs",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 7.5C4 6.12 5.12 5 6.5 5H19M8 19H17.5C18.88 19 20 17.88 20 16.5V7.5C20 6.12 18.88 5 17.5 5H8C6.62 5 5.5 6.12 5.5 7.5V16.5C5.5 17.88 6.62 19 8 19Z"
              stroke="var(--brand)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8.5 9H16.5M8.5 12H14.5"
              stroke="var(--brand-accent)"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        ),
        points: ["Workshops & coaching", "Standard work & routines", "Team adoption"]
      },
      {
        title: "Consulting",
        subtitle: "Improve performance",
        desc: "Optimize quality systems, reduce risk, and accelerate operational outcomes.",
        href: "/services",
        badge: "Engagements",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 19V5M4 19H20"
              stroke="var(--brand)"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M7 15L10.5 11.5L13 14L19 8"
              stroke="var(--brand-accent)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18.5 8H19V12"
              stroke="var(--brand-accent)"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        ),
        points: ["Quality system design", "Root cause reduction", "Sustained outcomes"]
      },
      {
        title: "Compliance",
        subtitle: "Stay audit-ready",
        desc: "Structured support to align processes, documentation, and governance.",
        href: "/services",
        badge: "Support",
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 3H15M9 3C7.9 3 7 3.9 7 5V7H17V5C17 3.9 16.1 3 15 3M7 7H17M7 7V19C7 20.1 7.9 21 9 21H15C16.1 21 17 20.1 17 19V7"
              stroke="var(--brand)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9 12L11 14L15.5 9.5"
              stroke="var(--brand-accent)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
        points: ["Audit readiness", "Documentation alignment", "Governance support"]
      }
    ],
    []
  );

  return (
    <section id="services" className="relative w-full overflow-hidden bg-white">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 h-[34rem] w-[34rem] rounded-full bg-[color:var(--brand)]/10 blur-3xl" />
        <div className="absolute -bottom-52 -left-52 h-[38rem] w-[38rem] rounded-full bg-[color:var(--brand-accent)]/12 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-18 sm:py-20">
        <div className="flex flex-col gap-3 max-w-2xl">
          <div className="inline-flex w-fit items-center gap-2 rounded-full bg-[color:var(--brand)]/10 px-4 py-2 text-sm font-semibold text-[color:var(--brand)]">
            Services
            <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-accent)]" />
            Built for outcomes
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[color:var(--navy)]">
            Modern quality support for teams that move fast
          </h2>
          <p className="text-[color:var(--muted)]">
            Clear engagement paths with practical deliverables and measurable improvements.
          </p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {items.map((it) => (
            <a
              key={it.title}
              href={it.href}
              className="group relative overflow-hidden rounded-2xl border border-[color:var(--border)] bg-[linear-gradient(180deg,rgba(245,247,250,1),rgba(255,255,255,1))] p-7 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-[color:var(--brand-accent)]/18 blur-3xl" />
                <div className="absolute -bottom-28 -left-28 h-64 w-64 rounded-full bg-[color:var(--brand)]/14 blur-3xl" />
              </div>

              <div className="relative flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-white border border-[color:var(--border)] flex items-center justify-center shadow-sm transition-transform duration-200 group-hover:scale-[1.05]">
                    {it.icon}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xl font-semibold text-[color:var(--navy)] truncate">
                      {it.title}
                    </div>
                    <div className="text-sm text-[color:var(--muted)]">
                      {it.subtitle}
                    </div>
                  </div>
                </div>

                <div className="rounded-full bg-[color:var(--brand)]/10 border border-[color:var(--brand)]/15 px-3 py-1 text-xs font-semibold text-[color:var(--brand)]">
                  {it.badge}
                </div>
              </div>

              <p className="relative mt-5 text-sm text-[color:var(--muted)] leading-relaxed">
                {it.desc}
              </p>

              <div className="relative mt-6 grid gap-2">
                {it.points.map((p) => (
                  <div key={p} className="flex items-center gap-2 text-sm text-[color:var(--navy)]/85">
                    <span className="h-2 w-2 rounded-full bg-[color:var(--brand-accent)]" />
                    <span>{p}</span>
                  </div>
                ))}
              </div>

              <div className="relative mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--brand)]">
                Learn more
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[color:var(--brand)]/10 transition group-hover:bg-[color:var(--brand)]/15">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12H19M19 12L13 6M19 12L13 18"
                      stroke="var(--brand)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>

              <div className="relative mt-7 h-px w-full bg-[color:var(--border)]" />
              <div className="relative mt-4 flex items-center justify-between text-xs">
                <span className="text-[color:var(--muted)]">Typical engagement</span>
                <span className="font-semibold text-[color:var(--navy)]">2–6 weeks</span>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-12 overflow-hidden rounded-2xl border border-[color:var(--border)] bg-[color:var(--navy)]">
          <div className="relative px-7 py-10 sm:px-10">
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[color:var(--brand-accent)]/18 blur-3xl" />
            <div className="absolute -bottom-28 -left-28 h-72 w-72 rounded-full bg-white/8 blur-3xl" />

            <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-2xl">
                <div className="text-sm font-semibold text-white/80">
                  Rapid start
                </div>
                <div className="mt-2 text-2xl sm:text-3xl font-semibold text-white">
                  Start with a quick discovery call
                </div>
                <p className="mt-2 text-white/75">
                  We’ll recommend the best path across training, consulting, or compliance support.
                </p>
              </div>

              <a
                href="/book"
                className="inline-flex items-center justify-center rounded-lg px-7 py-3 text-sm font-semibold bg-[color:var(--brand-accent)] text-[color:var(--navy)] hover:brightness-95 transition shadow-lg"
              >
                Book a Call
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesTeaser;
